import React from 'react';
import { motion } from 'framer-motion';
import { SmartphoneNfcIcon, MessageSquareIcon, TrendingUpIcon, ArrowRightIcon, RefreshCwIcon, ShieldCheckIcon, SparklesIcon, WalletIcon } from 'lucide-react';
import { Header } from '../Header';
import { SiteFooter } from '../SiteFooter';
import { FadeIn } from '../ui';
export function CanaryPage() {
  return <div className="min-h-screen bg-white font-sans text-slate-800 flex flex-col" data-id="element-4433">
      <Header data-id="element-4434" />

      <main className="flex-1" data-id="element-4435">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden bg-[#0A0F1F]" data-id="element-4436">
          {/* Ambient background */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-4437">
            <motion.div animate={{
            x: [0, 30, 0],
            y: [0, -20, 0]
          }} transition={{
            duration: 14,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-brand-teal/[0.15] rounded-full blur-[100px]" data-id="element-4438" />
            <motion.div animate={{
            x: [0, -25, 0],
            y: [0, 30, 0]
          }} transition={{
            duration: 16,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute top-1/4 -right-40 w-[500px] h-[500px] bg-brand-coral/[0.12] rounded-full blur-[100px]" data-id="element-4439" />
          </div>

          {/* Grid texture */}
          <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)'
        }} data-id="element-4440" />

          <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10 text-center" data-id="element-4441">
            <FadeIn data-id="element-4442">
              <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full pl-2 pr-4 py-1.5 mb-8 backdrop-blur-sm" data-id="element-4443">
                <span className="bg-brand-teal text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-4444">
                  Integration
                </span>
                <span className="text-sm font-medium text-slate-300" data-id="element-4445">
                  Offsite+ &amp; Canary Technologies
                </span>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-4446">
              <h1 className="text-5xl md:text-7xl font-bold leading-[1.1] tracking-tight text-white mb-6 text-balance" data-id="element-4447">
                A frictionless guest journey that{' '}
                <span className="text-brand-coral" data-id="element-4448">sells itself.</span>
              </h1>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-4449">
              <p className="text-lg md:text-xl text-slate-400 mb-10 leading-relaxed text-balance max-w-3xl mx-auto" data-id="element-4450">
                Offsite Plus pairs with Canary Technologies to turn contactless
                check-in, AI guest messaging, and dynamic upsells into a single,
                revenue-generating offsite experience — personalized for every
                attendee, automated for your team.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} data-id="element-4451">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16" data-id="element-4452">
                <a href="https://calendly.com/ashsingh/post-hitec-executive-briefing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-center leading-none whitespace-nowrap rounded-full font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed no-underline bg-brand-teal text-white hover:bg-teal-500 focus:ring-brand-teal shadow-lg shadow-brand-teal/20 text-base px-8 py-4 group w-full sm:w-auto" data-id="element-4453">
                  Book an Integration Briefing
                  <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-4454" />
                </a>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* Value Props */}
        <section className="py-24 bg-white" data-id="element-4455">
          <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-4456">
            <FadeIn data-id="element-4457">
              <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-4458">
                <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-4 tracking-tight" data-id="element-4459">
                  The guest experience layer, supercharged for groups.
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed" data-id="element-4460">
                  Canary handles the digital touchpoints and payments. Offsite+
                  fills them with personalized programming and revenue
                  opportunities — end to end.
                </p>
              </div>
            </FadeIn>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-4461">
              <FadeIn delay={0.1} data-id="element-4462">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 h-full hover:shadow-md transition-shadow" data-id="element-4463">
                  <div className="w-14 h-14 bg-brand-navy/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-4464">
                    <SmartphoneNfcIcon className="w-7 h-7 text-brand-navy" data-id="element-4465" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-4466">
                    Seamless Group Check-In
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-4467">
                    Attendees breeze through Canary's contactless check-in and
                    digital authorizations, landing straight into their
                    personalized Offsite+ agenda the moment they arrive.
                  </p>
                </div>
              </FadeIn>

              <FadeIn delay={0.2} data-id="element-4468">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 h-full hover:shadow-md transition-shadow" data-id="element-4469">
                  <div className="w-14 h-14 bg-brand-teal/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-4470">
                    <MessageSquareIcon className="w-7 h-7 text-brand-teal" data-id="element-4471" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-4472">
                    AI-Powered Guest Messaging
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-4473">
                    Offsite+ programming flows through Canary's AI guest
                    messaging — delivering session reminders, learning prompts,
                    and pod meetups in the channel guests already use.
                  </p>
                </div>
              </FadeIn>

              <FadeIn delay={0.3} data-id="element-4474">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 h-full hover:shadow-md transition-shadow" data-id="element-4475">
                  <div className="w-14 h-14 bg-brand-coral/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-4476">
                    <TrendingUpIcon className="w-7 h-7 text-brand-coral" data-id="element-4477" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-4478">
                    Smart Upsells That Convert
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-4479">
                    Context-aware spa, dining, and activity upsells fire through
                    Canary's Upsells engine at the perfect moment in the offsite
                    schedule — lifting ancillary revenue per guest.
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* Technical Integration */}
        <section className="py-24 bg-slate-50 border-t border-slate-100" data-id="element-4480">
          <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-4481">
            <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-4482">
              <FadeIn data-id="element-4483">
                <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-6 tracking-tight" data-id="element-4484">
                  Connected at every guest touchpoint.
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-4485">
                  From the pre-arrival message to the final folio, Offsite+ and
                  Canary share data in real time — so the right prompt reaches
                  the right guest, and every dollar is captured cleanly.
                </p>

                <ul className="space-y-6" data-id="element-4486">
                  {[{
                  icon: RefreshCwIcon,
                  title: 'Real-Time Event Sync',
                  desc: 'Check-in status, messages, and upsell conversions sync instantly between both platforms.'
                }, {
                  icon: SparklesIcon,
                  title: 'Personalized Triggers',
                  desc: 'Offsite+ schedule milestones trigger tailored Canary messages and offers automatically.'
                }, {
                  icon: ShieldCheckIcon,
                  title: 'Secure Payments & PCI',
                  desc: 'Canary handles digital authorizations and PCI-compliant payments while Offsite+ tracks the revenue.'
                }].map((item, i) => <li key={i} className="flex gap-4" data-id="element-4487">
                      <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-4488">
                        <item.icon className="w-5 h-5 text-brand-teal" data-id="element-4489" />
                      </div>
                      <div data-id="element-4490">
                        <h4 className="text-base font-bold text-brand-navy mb-1" data-id="element-4491">
                          {item.title}
                        </h4>
                        <p className="text-sm text-slate-600 leading-relaxed" data-id="element-4492">
                          {item.desc}
                        </p>
                      </div>
                    </li>)}
                </ul>
              </FadeIn>

              <FadeIn delay={0.2} data-id="element-4493">
                <div className="relative" data-id="element-4494">
                  <div className="absolute inset-0 bg-gradient-to-tr from-brand-teal/20 to-brand-coral/20 rounded-3xl blur-3xl transform rotate-3" data-id="element-4495" />
                  <div className="relative bg-white border border-slate-200 rounded-3xl p-8 shadow-xl" data-id="element-4496">
                    <div className="flex items-center justify-between mb-8 pb-6 border-b border-slate-100" data-id="element-4497">
                      <div className="font-bold text-xl text-brand-navy" data-id="element-4498">
                        Canary Guest App
                      </div>
                      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-brand-teal bg-brand-teal/10 px-2 py-1 rounded-full" data-id="element-4499">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-teal animate-pulse" data-id="element-4500" />
                        Live
                      </div>
                    </div>

                    <div className="space-y-4" data-id="element-4501">
                      <div className="h-20 bg-slate-50 rounded-xl border border-slate-100 flex items-center px-6 gap-4" data-id="element-4502">
                        <div className="w-11 h-11 rounded-full bg-brand-navy/10 flex items-center justify-center shrink-0" data-id="element-4503">
                          <SmartphoneNfcIcon className="w-5 h-5 text-brand-navy" data-id="element-4504" />
                        </div>
                        <div className="flex-1" data-id="element-4505">
                          <div className="text-sm font-bold text-brand-navy mb-1" data-id="element-4506">
                            Checked in · Room 412
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-4507">
                            Welcome to your Leadership Offsite
                          </div>
                        </div>
                      </div>

                      {/* Offsite+ message simulation */}
                      <div className="p-6 bg-brand-navy rounded-xl text-white relative overflow-hidden" data-id="element-4508">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-brand-coral/20 rounded-full blur-2xl transform translate-x-1/2 -translate-y-1/2" data-id="element-4509" />
                        <div className="flex items-center gap-2 mb-4" data-id="element-4510">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-brand-coral" data-id="element-4511">
                            From Offsite+
                          </span>
                        </div>
                        <div className="text-lg font-bold mb-2" data-id="element-4512">
                          Your 4pm session is in the Palm Courtyard
                        </div>
                        <div className="text-sm text-slate-300 mb-4" data-id="element-4513">
                          Add the sunset wellness session for $45?
                        </div>
                        <div className="h-10 bg-white/10 rounded-lg flex items-center px-4 backdrop-blur-sm border border-white/5" data-id="element-4514">
                          <span className="text-sm font-medium" data-id="element-4515">
                            Add to my stay
                          </span>
                          <ArrowRightIcon className="w-4 h-4 ml-auto" data-id="element-4516" />
                        </div>
                      </div>

                      <div className="h-20 bg-slate-50 rounded-xl border border-slate-100 flex items-center px-6 gap-4" data-id="element-4517">
                        <div className="w-11 h-11 rounded-full bg-brand-coral/10 flex items-center justify-center shrink-0" data-id="element-4518">
                          <WalletIcon className="w-5 h-5 text-brand-coral" data-id="element-4519" />
                        </div>
                        <div className="flex-1" data-id="element-4520">
                          <div className="text-sm font-bold text-brand-navy mb-1" data-id="element-4521">
                            Upsell confirmed · +$45
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-4522">
                            Posted securely via Canary
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-white text-center" data-id="element-4523">
          <div className="max-w-3xl mx-auto px-6 md:px-12" data-id="element-4524">
            <FadeIn data-id="element-4525">
              <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-6" data-id="element-4526">
                Ready to delight guests and grow revenue?
              </h2>
              <p className="text-lg text-slate-600 mb-10" data-id="element-4527">
                Let's walk through how the Offsite+ and Canary Technologies
                integration creates a seamless, high-converting guest experience
                for every corporate offsite at your property.
              </p>
              <a href="https://calendly.com/ashsingh/post-hitec-executive-briefing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-center leading-none whitespace-nowrap rounded-full font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 no-underline bg-brand-navy text-white hover:bg-slate-800 focus:ring-brand-navy shadow-lg text-base px-8 py-4 group" data-id="element-4528">
                Schedule a Demo
                <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-4529" />
              </a>
            </FadeIn>
          </div>
        </section>
      </main>

      <SiteFooter data-id="element-4530" />
    </div>;
}